import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Tag, MessageCircle, Eye } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import StarRating from "@/components/StarRating";

const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");

// Deterministic hue picker so a given business always renders with the same
// accent color when it has no photos yet.
const stringHash = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};

// Warm palette — teal/orange family from the design system
const GRADIENT_STOPS = [
  ["from-teal-deep", "to-teal"],
  ["from-teal", "to-orange"],
  ["from-orange", "to-yellow-500"],
  ["from-teal-deep", "to-orange"],
  ["from-teal-soft", "to-teal-deep"],
  ["from-orange", "to-pink-500"],
];

const gradientFor = (name = "") => {
  const idx = stringHash(name) % GRADIENT_STOPS.length;
  const [from, to] = GRADIENT_STOPS[idx];
  return `${from} ${to}`;
};

/**
 * Facebook-style business card.
 *  ┌─────────────────────────┐
 *  │      cover 2.7:1        │
 *  │                         │
 *  │   ⓜ                     │  ← round logo overlapping bottom-left
 *  ├─────────────────────────┤
 *  │ Name                    │
 *  │ Category · City         │
 *  │ Description (2 lines)   │
 *  │ ★★★★★                    │
 *  │ [ Ver perfil ][ WA ]    │
 *  └─────────────────────────┘
 */
export const BusinessCard = ({ biz }) => {
  const { t } = useI18n();
  const cat = (t.categories && t.categories[biz.category]) || biz.category;
  const coverPos = biz.cover_position ?? 50;
  const waNumber = (biz.whatsapp || "").replace(/\D/g, "");
  const hasCover = !!biz.cover_url;
  const hasLogo = !!biz.logo_url;
  const gradient = gradientFor(biz.business_name || biz.id || "");

  return (
    <div
      className="bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col group shadow-sm hover:shadow-md transition-shadow"
      data-testid={`biz-card-${biz.id}`}
    >
      {/* Cover */}
      <Link to={`/entrepreneur/${biz.id}`} className="block relative aspect-[2.7/1] overflow-hidden">
        {hasCover ? (
          <img
            src={biz.cover_url}
            alt=""
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            style={{ objectPosition: `center ${coverPos}%` }}
          />
        ) : (
          <div className={`relative w-full h-full bg-gradient-to-br ${gradient} flex items-center justify-center overflow-hidden`}>
            {/* Soft radial decoration */}
            <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/10 blur-2xl" aria-hidden />
            <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-black/10 blur-2xl" aria-hidden />
            {/* Dark scrim so the name always has enough contrast on light gradients */}
            <div className="absolute inset-0 bg-black/25" aria-hidden />
            <span
              className="relative z-10 font-display text-white tracking-wide text-2xl md:text-3xl px-6 text-center line-clamp-2 drop-shadow-md"
              data-testid={`biz-cover-empty-${biz.id}`}
            >
              {biz.business_name}
            </span>
          </div>
        )}
        {biz.featured && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-orange text-white text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-0.5 shadow">
            ★ Destacado
          </span>
        )}
      </Link>

      {/* Content */}
      <div className="px-4 pb-4 pt-0 flex flex-col flex-1 relative">
        {/* Avatar overlapping cover */}
        <Link
          to={`/entrepreneur/${biz.id}`}
          className="absolute -top-8 left-4 w-16 h-16 rounded-full overflow-hidden border-[3px] border-white shadow-md"
          aria-label={biz.business_name}
        >
          {hasLogo ? (
            <img src={biz.logo_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className={`w-full h-full flex items-center justify-center font-display text-white text-lg bg-gradient-to-br ${gradient}`}>
              {initials(biz.business_name) || "?"}
            </span>
          )}
        </Link>

        <div className="mt-10">
          <Link to={`/entrepreneur/${biz.id}`}>
            <h3 className="font-display text-lg text-teal-deep leading-tight truncate hover:text-orange transition-colors">
              {biz.business_name}
            </h3>
          </Link>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-teal-soft mt-1">
            {cat && (
              <span className="inline-flex items-center gap-1"><Tag size={11} /> {cat}</span>
            )}
            {biz.city && (
              <span className="inline-flex items-center gap-1">
                <MapPin size={11} /> {biz.city}{biz.state ? `, ${biz.state}` : ""}
              </span>
            )}
          </div>
        </div>

        {biz.description && (
          <p className="text-sm text-teal-soft line-clamp-2 leading-relaxed mt-3">
            {biz.description}
          </p>
        )}

        <div className="mt-3 flex items-center justify-between text-xs text-teal-soft">
          {(biz.ratings_count > 0 || biz.avg_rating > 0) ? (
            <StarRating value={biz.avg_rating || 0} count={biz.ratings_count || 0} showValue size={13} />
          ) : (
            <span className="italic">Aún sin reseñas</span>
          )}
          {(biz.view_count || 0) > 0 && (
            <span className="inline-flex items-center gap-1">
              <Eye size={11} /> {biz.view_count}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="mt-auto pt-3 border-t border-gray-100 flex items-center gap-2">
          <Link
            to={`/entrepreneur/${biz.id}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-cream text-teal-deep hover:bg-teal-deep hover:text-white rounded-lg py-2.5 transition-colors"
            data-testid={`biz-view-${biz.id}`}
          >
            {t.directory.view}
          </Link>
          {waNumber && (
            <a
              href={`https://wa.me/${waNumber}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#25D366] hover:bg-[#1fb457] text-white rounded-lg px-4 py-2.5 transition-colors"
              data-testid={`biz-wa-${biz.id}`}
              aria-label="WhatsApp"
            >
              <MessageCircle size={13} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export const CategoryChip = ({ value, label, active, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(value)}
    className={`chip ${active ? "active" : ""}`}
    data-testid={`chip-${value}`}
  >
    {label}
  </button>
);
